import React, { ReactNode } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
} from '@mui/material';
import {
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Enhanced PageCard Props Interface
interface PageCardProps {
  // Core props
  title: string;
  description?: string;
  children?: ReactNode;
  
  // Layout & sizing
  maxWidth?: number | string;
  variant?: 'default' | 'compact' | 'fullscreen' | 'minimal';
  elevation?: number;
  
  // Header customization
  showHeader?: boolean;
  headerActions?: HeaderAction[];
  headerBadges?: HeaderBadge[];
  
  // Navigation
  breadcrumbs?: BreadcrumbItem[];
  showHomeBreadcrumb?: boolean;
  
  // States
  loading?: boolean;
  error?: string | null;
  empty?: boolean;
  emptyMessage?: string;
  emptyAction?: ReactNode;
  
  // Functionality
  onRefresh?: () => void;
  onFullscreenToggle?: () => void;
  isFullscreen?: boolean;
  
  // Styling
  headerColor?: string;
  accentColor?: string;
  customStyles?: object;
  
  // Accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
}

// Type definitions
interface HeaderAction {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  disabled?: boolean;
  tooltip?: string;
  size?: 'small' | 'medium' | 'large';
}

interface HeaderBadge {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  variant?: 'filled' | 'outlined';
  icon?: ReactNode;
}

interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
}

// Styled Components
const StyledPageCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  position: 'relative',
  
  '&:hover': {
    boxShadow: theme.shadows[12],
    transform: 'translateY(-4px)',
    borderColor: theme.palette.primary.main,
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '&:hover::before': {
    opacity: 1,
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.primary.main} 100%)`,
  backgroundSize: '200% 200%',
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3, 4),
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '120px',
    height: '120px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    transform: 'translate(40px, -40px)',
    animation: 'pulse 3s ease-in-out infinite',
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '80px',
    height: '80px',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '50%',
    transform: 'translate(-20px, 20px)',
  },
  
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.1, transform: 'translate(40px, -40px) scale(1)' },
    '50%': { opacity: 0.2, transform: 'translate(40px, -40px) scale(1.1)' },
  },
  
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2, 3),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2.5),
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'left 0.5s ease',
  },
  
  '&:hover::before': {
    left: '100%',
  },
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const BreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
    transform: 'translateX(2px)',
  },
}));

const LoadingSkeleton = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const ErrorContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.error.main,
  background: `linear-gradient(135deg, ${theme.palette.error.light} 0%, ${theme.palette.background.paper} 100%)`,
  borderRadius: theme.spacing(2),
  border: `2px solid ${theme.palette.error.main}`,
}));

const EmptyContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
  borderRadius: theme.spacing(2),
  border: `2px dashed ${theme.palette.divider}`,
}));

// Enhanced PageCard Component
export default function PageCard({
  // Core props
  title,
  description,
  children,
  
  // Layout & sizing
  maxWidth = 1400,
  variant = 'default',
  elevation = 2,
  
  // Header customization
  showHeader = true,
  headerActions = [],
  headerBadges = [],
  
  // Navigation
  breadcrumbs = [],
  showHomeBreadcrumb = true,
  
  // States
  loading = false,
  error = null,
  empty = false,
  emptyMessage = 'No data available',
  emptyAction,
  
  // Functionality
  onRefresh,
  onFullscreenToggle,
  isFullscreen = false,
  
  // Styling
  headerColor,
  accentColor,
  customStyles = {},
  
  // Accessibility
  ariaLabel,
  ariaDescribedBy,
}: PageCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = variant === 'compact';
  const isFullscreenVariant = variant === 'fullscreen';

  // Enhanced breadcrumbs with home icon
  const enhancedBreadcrumbs = [
    ...(showHomeBreadcrumb ? [{ label: 'Home', href: '/', icon: <HomeIcon fontSize="small" /> }] : []),
    ...breadcrumbs,
  ];

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth, mx: 'auto', py: { xs: 1, md: 2 }, px: { xs: 1, sm: 2 } }}>
        <LoadingSkeleton>
          <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="40%" height={24} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        </LoadingSkeleton>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth, mx: 'auto', py: { xs: 1, md: 2 }, px: { xs: 1, sm: 2 } }}>
        <ErrorContainer>
          <Typography variant="h6" color="error" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
          {onRefresh && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          )}
        </ErrorContainer>
      </Box>
    );
  }

  // Render empty state
  if (empty) {
    return (
      <Box sx={{ width: '100%', maxWidth, mx: 'auto', py: { xs: 1, md: 2 }, px: { xs: 1, sm: 2 } }}>
        <EmptyContainer>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {emptyMessage}
          </Typography>
          {emptyAction}
        </EmptyContainer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: isFullscreenVariant ? '100%' : maxWidth,
        mx: 'auto',
        py: isFullscreenVariant ? 0 : { xs: 1, md: 2 },
        px: isFullscreenVariant ? 0 : { xs: 1, sm: 2 },
        minHeight: isFullscreenVariant ? '100vh' : 'auto',
        ...customStyles,
      }}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
    >
      {/* Breadcrumbs */}
      {enhancedBreadcrumbs.length > 0 && (
        <Box sx={{ mb: 2, px: { xs: 1, sm: 2 } }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}
          >
            {enhancedBreadcrumbs.map((item, index) => (
              <BreadcrumbLink
                key={index}
                href={item.href}
                onClick={('onClick' in item) ? item.onClick : undefined}
                color="inherit"
                underline="hover"
              >
                {item.icon}
                {item.label}
              </BreadcrumbLink>
            ))}
          </Breadcrumbs>
        </Box>
      )}

      {/* Enhanced Header */}
      {showHeader && (
        <Slide direction="down" in={true} timeout={500}>
          <HeaderContainer
            sx={{
              background: headerColor || `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.primary.main} 100%)`,
              mb: isCompact ? 2 : 3,
              borderRadius: isFullscreenVariant ? 0 : theme.spacing(2),
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
              {/* Title Section */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant={isCompact ? 'h5' : 'h4'}
                  sx={{
                    fontWeight: 700,
                    color: 'inherit',
                    mb: description ? 1 : 0,
                    lineHeight: 1.2,
                  }}
                >
                  {title}
                </Typography>
                {description && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: isCompact ? '14px' : '16px',
                      fontWeight: 400,
                      maxWidth: '600px',
                    }}
                  >
                    {description}
                  </Typography>
                )}
                
                {/* Header Badges */}
                {headerBadges.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {headerBadges.map((badge, index) => (
                      <Chip
                        key={index}
                        label={badge.label}
                        color={badge.color}
                        variant={badge.variant || 'filled'}
                        icon={badge.icon as React.ReactElement}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'inherit',
                          '& .MuiChip-label': { fontWeight: 500 },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Refresh Button */}
                {onRefresh && (
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={onRefresh}
                      sx={{ color: 'inherit', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Fullscreen Toggle */}
                {onFullscreenToggle && (
                  <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}>
                    <IconButton
                      onClick={onFullscreenToggle}
                      sx={{ color: 'inherit', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    >
                      {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </IconButton>
                  </Tooltip>
                )}

                {/* Header Actions */}
                {headerActions.map((action, index) => (
                  <Tooltip key={index} title={action.tooltip || action.label}>
                    <span>
                      <ActionButton
                        variant={action.variant || 'contained'}
                        color={action.color || 'primary'}
                        size={action.size || 'medium'}
                        startIcon={action.icon}
                        onClick={action.onClick}
                        disabled={action.disabled}
                        sx={{
                          backgroundColor: action.variant === 'contained' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                          color: 'inherit',
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            backgroundColor: action.variant === 'contained' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        {!isMobile && action.label}
                      </ActionButton>
                    </span>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </HeaderContainer>
        </Slide>
      )}

      {/* Main Content Card */}
      <Fade in={true} timeout={800}>
        <StyledPageCard
          elevation={elevation}
          sx={{
            border: accentColor ? `2px solid ${accentColor}` : undefined,
          }}
        >
          <CardContent
            sx={{
              p: isCompact ? 2 : { xs: 2, sm: 3 },
              '&:last-child': { pb: isCompact ? 2 : { xs: 2, sm: 3 } },
            }}
          >
            {children}
          </CardContent>
        </StyledPageCard>
      </Fade>
    </Box>
  );
}

// Export types for external use
export type { PageCardProps, HeaderAction, HeaderBadge, BreadcrumbItem };